import { useCallback, useEffect, useRef, useState } from 'react';
import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import { chainCoverSave } from '../../coverEditor/doc/chainCoverSave';
import { useUpdateMusicVideoCutMutation } from '../musicVideoEditorApi';
import { cutSaveError } from './musicVideoErrors';

/** The wait after the last change before the cut is saved (the cover editor rule). */
export const CUT_AUTOSAVE_MS = 1200;

/**
 * `stale`: another save came first (409 `M24_CUT_STALE`); the editor reads the cut again.
 * `working`: the director writes the cut (409 `M24_CUT_WORKING`).
 * `invalid`: the API refused the document (`M24_CUT_DOC_INVALID`); the same save fails again.
 */
export type CutSaveState = 'saved' | 'dirty' | 'saving' | 'failed' | 'invalid' | 'stale' | 'working';

interface UseCutAutosaveProps {
  accountId: number;
  catalogItemUuid: string;
  cutUuid: string;
  doc: MusicVideoDoc;
  /** Goes up with each change of the document (`musicVideoDocReducer`). */
  revision: number;
  /** The revision of the cut on the server when the editor opened it. */
  serverRevision: number;
  /** False for the read-only demo artist and while the director works: nothing is saved. */
  enabled: boolean;
  /** A save was refused as stale: the editor reads the newest cut and shows a note. */
  onStale: () => void;
}

/** The most saves one flush sends while the user keeps changing the cut during them. */
const FLUSH_MAX_ROUNDS = 3;

/** True when the server holds the open document: a make, a recut or a copy may read it. Pure. */
export function isCutSaved(state: CutSaveState): boolean {
  return state === 'saved';
}

export interface CutAutosave {
  saveState: CutSaveState;
  /**
   * Saves at once and waits (before a cut switch, "Make the video", a recut, a copy, a leave). It
   * answers the save state after the save: only `saved` means that the server holds the open
   * document. Any other state is the reason it does not (the top bar shows the same words).
   */
  flush: () => Promise<CutSaveState>;
  /** Goes up with each stale save (409): the editor loads the cut of the server again. */
  staleCount: number;
  hasUnsaved: boolean;
  /** The server holds this document now (a load from the server): nothing to send. */
  markSaved: (revision: number, serverRevision: number) => void;
  /** The newest revision of the cut on the server that this editor knows. */
  serverRevisionRef: { readonly current: number };
}

/**
 * Saves the open cut by itself (design music-video b1, "Saved"): `CUT_AUTOSAVE_MS` after the last
 * change it sends `PATCH .../cuts/:cutUuid { revision, doc }`. One save runs at a time; a change
 * during a save is saved after it. Each answer gives the next server revision. A 409 stale stops the
 * saves and asks the editor to read the cut again (spec §4.4). A leave of the page saves the last change too.
 */
export function useCutAutosave(props: UseCutAutosaveProps): CutAutosave {
  const { accountId, catalogItemUuid, cutUuid, doc, revision, serverRevision, enabled, onStale } = props;
  const [updateCut] = useUpdateMusicVideoCutMutation();
  const [saveState, setSaveStateValue] = useState<CutSaveState>('saved');
  const [staleCount, setStaleCount] = useState(0);
  // The state as the last write left it: `flush` answers it after its await, before React renders.
  const stateRef = useRef<CutSaveState>('saved');
  const setSaveState = useCallback((state: CutSaveState) => {
    stateRef.current = state;
    setSaveStateValue(state);
  }, []);
  const latest = useRef({ doc, revision });
  latest.current = { doc, revision };
  const savedRevision = useRef(revision);
  const serverRevisionRef = useRef(serverRevision);
  const running = useRef<Promise<void>>(Promise.resolve());
  const isStale = useRef(false);
  const onStaleRef = useRef(onStale);
  onStaleRef.current = onStale;

  const sendLatest = useCallback(async (): Promise<void> => {
    if (isStale.current || latest.current.revision === savedRevision.current) {
      return;
    }
    const sent = latest.current;
    setSaveState('saving');
    try {
      const { cut } = await updateCut({
        accountId,
        catalogItemUuid,
        cutUuid,
        revision: serverRevisionRef.current,
        doc: sent.doc,
      }).unwrap();
      serverRevisionRef.current = cut.revision;
      savedRevision.current = Math.max(savedRevision.current, sent.revision);
      setSaveState(latest.current.revision === savedRevision.current ? 'saved' : 'dirty');
    } catch (error: unknown) {
      const kind = cutSaveError(error);
      console.error('Music video editor: the cut was not saved', error);
      setSaveState(kind);
      if (kind === 'stale') {
        isStale.current = true;
        setStaleCount((count) => count + 1);
        onStaleRef.current();
      }
    }
  }, [accountId, catalogItemUuid, cutUuid, setSaveState, updateCut]);

  const save = useCallback(async (): Promise<void> => {
    if (!enabled) {
      return;
    }
    const next = chainCoverSave(running.current, sendLatest);
    running.current = next;
    await next;
  }, [enabled, sendLatest]);

  // A change during a save leaves the cut `dirty`: the flush sends it too, a few rounds at most.
  const flush = useCallback(async (): Promise<CutSaveState> => {
    for (let round = 0; round < FLUSH_MAX_ROUNDS; round += 1) {
      // eslint-disable-next-line no-await-in-loop -- one save at a time, each after the one before
      await save();
      if (!enabled || stateRef.current !== 'dirty') {
        break;
      }
    }
    return stateRef.current;
  }, [enabled, save]);

  useEffect(() => {
    if (!enabled || revision === savedRevision.current) {
      return undefined;
    }
    // A stale cut keeps its state until the newest cut loads (`useServerCutSync` loads only then).
    if (stateRef.current !== 'saving' && !isStale.current) {
      setSaveState('dirty');
    }
    const timer = window.setTimeout(() => {
      save().catch(console.error);
    }, CUT_AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, revision, save, setSaveState]);

  const markSaved = useCallback(
    (localRevision: number, nextServerRevision: number) => {
      savedRevision.current = localRevision;
      serverRevisionRef.current = nextServerRevision;
      isStale.current = false;
      setSaveState('saved');
    },
    [setSaveState]
  );

  // The last change goes out when the editor closes (a leave, another cut).
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      saveRef.current().catch(console.error);
    },
    []
  );

  return { saveState, flush, staleCount, hasUnsaved: saveState !== 'saved', markSaved, serverRevisionRef };
}
